import throttle from 'lodash/throttle'
import { MouseEvent as ReactMouseEvent, ReactNode, useRef, useState } from 'react'

type HoverIntentProps = {
  cancellableTimeoutCallback?: (timeout: number) => void
  children: ReactNode
  exitTimeout?: number
  onEnter?: () => void
  onExit?: () => void
  onUpdate?: (active: boolean) => void
  sensitivity?: number
}

type MousePosition = {
  x: number
  y: number
}

export const COMPARE_INTERVAL = 40
export const DEFAULT_EXIT_TIMEOUT = 250

const HoverIntent = ({
  children,
  exitTimeout = DEFAULT_EXIT_TIMEOUT,
  onEnter = () => {},
  onExit = () => {},
  onUpdate = () => {},
  sensitivity = 6,
  cancellableTimeoutCallback = () => {},
}: HoverIntentProps) => {
  const mousePosition = useRef<MousePosition>({ x: 0, y: 0 })
  const [active, setActive] = useState(false)

  const handleMouseMove = (event: ReactMouseEvent<HTMLSpanElement, MouseEvent>) => {
    compareMouseMove(event)
  }
  const compareMouseMove = throttle(
    ({ clientX, clientY }: ReactMouseEvent<HTMLSpanElement, MouseEvent>) => {
      const { x, y } = mousePosition.current
      const distance = Math.sqrt((x - clientX) * (x - clientX) + (y - clientY) * (y - clientY))
      if (distance < sensitivity) {
        onEnter()
        onUpdate(true)
        setActive(true)
      }
      mousePosition.current = { x: clientX, y: clientY }
    },
    COMPARE_INTERVAL,
  )

  const handleMouseLeave = () => {
    if (active) {
      // the reference to the setTimeout is passed back to the parent component
      // when a timeout is triggered so it could be cancelled if and when necessary
      // to prevent the hover behavior from resetting on mouse leave
      cancellableTimeoutCallback(
        window.setTimeout(() => {
          onExit()
          onUpdate(false)
          setActive(false)
        }, exitTimeout),
      )
    }
  }

  return (
    <span onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      {children}
    </span>
  )
}

export default HoverIntent
